/* Clean Slate license check — see snippets/license-check.liquid for context.
 *
 *  - Reads config from #csp-license-config (JSON script tag).
 *  - Caches verdict in localStorage for 7 days keyed by shop+product+api.
 *  - Network/server failures → fail-OPEN on storefront (do nothing).
 *  - Live storefront unbound → small dismissable banner.
 *  - Live storefront revoked → persistent banner + watermark.
 *  - Theme editor unbound OR revoked → fullscreen blocking overlay so
 *    the merchant can't customize without activating.
 *
 *  No inline event handlers, no eval, no third-party deps.
 */
(function () {
  'use strict';

  var CACHE_KEY_PREFIX = 'cspLicense:v1:';
  var CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
  var FETCH_TIMEOUT_MS = 2500;
  var BANNER_DISMISS_KEY = 'cspLicenseBannerDismissed:v1';

  function inEditor() {
    return !!(window.Shopify && window.Shopify.designMode === true);
  }

  function getConfig() {
    var el = document.getElementById('csp-license-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent || el.innerText || '{}'); }
    catch (e) { return null; }
  }

  function cacheKey(cfg) {
    return CACHE_KEY_PREFIX + cfg.shop + ':' + (cfg.product_id || '_') + ':' + cfg.api;
  }

  function readCache(cfg) {
    try {
      var raw = localStorage.getItem(cacheKey(cfg));
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.expires !== 'number') return null;
      if (Date.now() > parsed.expires) return null;
      return parsed.verdict || null;
    } catch (e) { return null; }
  }

  function writeCache(cfg, verdict) {
    try {
      localStorage.setItem(cacheKey(cfg), JSON.stringify({
        verdict: verdict,
        expires: Date.now() + CACHE_TTL_MS
      }));
    } catch (e) { /* quota errors etc — ignore */ }
  }

  function fetchWithTimeout(url, ms) {
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, ms);
    return fetch(url, { signal: ctrl.signal, credentials: 'omit', cache: 'no-store' })
      .then(function (res) {
        clearTimeout(timer);
        if (!res.ok) throw new Error('http_' + res.status);
        return res.json();
      })
      .catch(function (err) {
        clearTimeout(timer);
        throw err;
      });
  }

  function buildVerifyUrl(cfg) {
    var u = cfg.api.replace(/\/+$/, '') + '/verify?shop=' + encodeURIComponent(cfg.shop);
    if (cfg.product_id) u += '&product=' + encodeURIComponent(cfg.product_id);
    return u;
  }

  function injectStyles() {
    if (document.getElementById('csp-license-banner-styles')) return;
    var style = document.createElement('style');
    style.id = 'csp-license-banner-styles';
    style.textContent = [
      /* Storefront banner */
      '.csp-license-banner{position:fixed;left:0;right:0;bottom:0;z-index:2147483646;',
      'padding:12px 16px;font:14px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;',
      'background:#fef3c7;color:#78350f;border-top:1px solid #fcd34d;',
      'display:flex;gap:12px;align-items:center;justify-content:center;flex-wrap:wrap}',
      '.csp-license-banner--revoked{background:#fee2e2;color:#7f1d1d;border-top-color:#fca5a5}',
      '.csp-license-banner a{color:inherit;text-decoration:underline;font-weight:600}',
      '.csp-license-banner button{appearance:none;background:transparent;border:1px solid currentColor;',
      'border-radius:4px;padding:4px 10px;font:inherit;color:inherit;cursor:pointer}',
      '.csp-license-banner button:hover{opacity:0.7}',
      /* Storefront revoked watermark */
      '.csp-license-watermark{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-30deg);',
      'font:bold 96px/1 -apple-system,sans-serif;color:rgba(220,38,38,0.18);pointer-events:none;',
      'z-index:2147483645;letter-spacing:8px;text-transform:uppercase;user-select:none;white-space:nowrap}',
      /* Theme editor fullscreen overlay */
      '.csp-license-overlay{position:fixed;inset:0;z-index:2147483647;',
      'background:rgba(15,15,15,0.92);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);',
      'display:flex;align-items:center;justify-content:center;padding:24px;',
      'font:15px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}',
      '.csp-license-overlay__card{background:#fff;color:#111;max-width:480px;width:100%;',
      'border-radius:12px;padding:32px 28px;text-align:center;',
      'box-shadow:0 20px 60px rgba(0,0,0,0.5)}',
      '.csp-license-overlay__icon{font-size:40px;line-height:1;margin-bottom:12px}',
      '.csp-license-overlay__title{font-size:22px;font-weight:700;margin:0 0 8px}',
      '.csp-license-overlay__body{font-size:15px;color:#555;margin:0 0 24px;line-height:1.5}',
      '.csp-license-overlay__cta{display:inline-block;background:#111;color:#fff;text-decoration:none;',
      'padding:12px 24px;border-radius:8px;font-weight:600;font-size:15px;margin-bottom:12px}',
      '.csp-license-overlay__cta:hover{background:#000}',
      '.csp-license-overlay__shop{display:block;font-family:"SF Mono",Menlo,monospace;',
      'font-size:13px;color:#666;background:#f5f5f5;padding:8px 12px;border-radius:6px;margin:12px 0;word-break:break-all}',
      '.csp-license-overlay__support{font-size:13px;color:#999;margin:16px 0 0}',
      '.csp-license-overlay__support a{color:#666}',
      '.csp-license-overlay--revoked .csp-license-overlay__card{border-top:4px solid #dc2626}',
      '.csp-license-overlay--revoked .csp-license-overlay__title{color:#7f1d1d}'
    ].join('');
    document.head.appendChild(style);
  }

  function clearArtifacts() {
    document.querySelectorAll(
      '.csp-license-banner, .csp-license-watermark, .csp-license-overlay'
    ).forEach(function (n) { n.remove(); });
  }

  /* ─── Editor: fullscreen blocking overlay ──────────────────────────────── */
  function showEditorOverlay(cfg, status) {
    injectStyles();
    var overlay = document.createElement('div');
    overlay.className = 'csp-license-overlay' + (status === 'revoked' ? ' csp-license-overlay--revoked' : '');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    var card = document.createElement('div');
    card.className = 'csp-license-overlay__card';

    var icon = document.createElement('div');
    icon.className = 'csp-license-overlay__icon';
    icon.textContent = status === 'revoked' ? '⛔' : '🔒';
    card.appendChild(icon);

    var title = document.createElement('h2');
    title.className = 'csp-license-overlay__title';
    title.textContent = status === 'revoked'
      ? 'This license has been revoked'
      : 'Activate your ' + (cfg.theme_name || 'Clean Slate') + ' license to start editing';
    card.appendChild(title);

    var body = document.createElement('p');
    body.className = 'csp-license-overlay__body';
    body.textContent = status === 'revoked'
      ? 'Editing is disabled while this license is revoked. Contact support if this is a mistake.'
      : 'Enter your license key on the activation page below. Use this exact store URL:';
    card.appendChild(body);

    if (status !== 'revoked') {
      var shop = document.createElement('code');
      shop.className = 'csp-license-overlay__shop';
      shop.textContent = cfg.shop;
      card.appendChild(shop);
    }

    var cta = document.createElement('a');
    cta.className = 'csp-license-overlay__cta';
    cta.href = cfg.activation_url;
    cta.target = '_blank';
    cta.rel = 'noopener';
    cta.textContent = status === 'revoked' ? 'Contact support' : 'Activate license →';
    if (status === 'revoked') cta.href = 'mailto:' + cfg.support_email;
    card.appendChild(cta);

    var support = document.createElement('p');
    support.className = 'csp-license-overlay__support';
    var supportLink = document.createElement('a');
    supportLink.href = 'mailto:' + cfg.support_email;
    supportLink.textContent = cfg.support_email;
    support.appendChild(document.createTextNode('Need help? '));
    support.appendChild(supportLink);
    card.appendChild(support);

    overlay.appendChild(card);
    document.body.appendChild(overlay);
  }

  /* ─── Storefront: friendly dismissable banner ──────────────────────────── */
  function showUnboundBanner(cfg) {
    if (sessionStorage.getItem(BANNER_DISMISS_KEY) === '1') return;
    injectStyles();
    var bar = document.createElement('div');
    bar.className = 'csp-license-banner';
    bar.setAttribute('role', 'status');

    var msg = document.createElement('span');
    msg.textContent = 'This Clean Slate theme is unlicensed. ';
    var link = document.createElement('a');
    link.href = cfg.activation_url;
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = 'Activate your license →';
    msg.appendChild(link);
    bar.appendChild(msg);

    var dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.textContent = 'Dismiss';
    dismiss.addEventListener('click', function () {
      try { sessionStorage.setItem(BANNER_DISMISS_KEY, '1'); } catch (e) {}
      bar.remove();
    });
    bar.appendChild(dismiss);

    document.body.appendChild(bar);
  }

  /* ─── Storefront: persistent lockout for revoked ───────────────────────── */
  function showRevokedLockout(cfg) {
    injectStyles();
    var bar = document.createElement('div');
    bar.className = 'csp-license-banner csp-license-banner--revoked';
    bar.setAttribute('role', 'alert');
    var msg = document.createElement('span');
    msg.appendChild(document.createTextNode('This Clean Slate license has been revoked. Contact '));
    var mail = document.createElement('a');
    mail.href = 'mailto:' + cfg.support_email;
    mail.textContent = cfg.support_email;
    msg.appendChild(mail);
    msg.appendChild(document.createTextNode(' for help.'));
    bar.appendChild(msg);
    document.body.appendChild(bar);

    var watermark = document.createElement('div');
    watermark.className = 'csp-license-watermark';
    watermark.textContent = 'UNLICENSED';
    watermark.setAttribute('aria-hidden', 'true');
    document.body.appendChild(watermark);
  }

  function apply(verdict, cfg) {
    if (!verdict || !verdict.status) return;
    var status = verdict.status;
    var editor = inEditor();

    if (status === 'active') return;

    if (editor) {
      /* Editor: any non-active status → fullscreen blocking overlay */
      showEditorOverlay(cfg, status);
    } else {
      /* Storefront: gentler treatment */
      if (status === 'revoked') showRevokedLockout(cfg);
      else if (status === 'unbound') showUnboundBanner(cfg);
    }
  }

  function init() {
    var cfg = getConfig();
    if (!cfg || !cfg.api || !cfg.shop) return;

    /* Use cached verdict immediately if fresh (offline-resilient). */
    var cached = readCache(cfg);
    if (cached) apply(cached, cfg);

    /* Always background-refresh so revocations propagate within 7 days. */
    fetchWithTimeout(buildVerifyUrl(cfg), FETCH_TIMEOUT_MS)
      .then(function (fresh) {
        if (!fresh || !fresh.status) return;
        writeCache(cfg, fresh);
        if (!cached || cached.status !== fresh.status) {
          clearArtifacts();
          apply(fresh, cfg);
        }
      })
      .catch(function () {
        /* Fail-OPEN on storefront. In editor we ALSO fail-open on network
           errors — otherwise merchants get locked out by transient outages.
           Only an explicit revoked/unbound verdict from our server triggers
           the editor overlay. */
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
